import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Schema for individual Oracle order number result.
 */
const oracleOrderResultSchema = z.object({
  salesOrderId: z.string().uuid(),
  stagedPurchaseOrderId: z.string().uuid().nullable(),
  oracleOrderNumber: z.string().nullable(),
  origSysDocRef: z.string().nullable(),
  found: z.boolean(),
});

/**
 * Await Oracle Order Numbers Step
 *
 * Polls Oracle EBS to retrieve assigned order numbers for submitted sales orders.
 * Uses exponential backoff to wait for Oracle to process the orders and assign
 * order numbers.
 *
 * This step is designed to run after the submitSalesOrders step to allow time
 * for Oracle's asynchronous order import process to complete.
 *
 * The step will:
 * 1. Load sales orders to check current originMetadata and origSysDocRef
 * 2. Poll Oracle via fetchOrderStatuses() with exponential backoff
 * 3. Return results with whatever data is available when polling completes
 *
 * Polling exits when:
 * - All orders have Oracle order numbers assigned
 * - Maximum wait time is exceeded
 */
export const awaitOracleOrderNumbersStep = defineStepConfig({
  type: "await_oracle_order_numbers",
  name: "Await Oracle Order Numbers",
  description:
    "Polls Oracle EBS to retrieve assigned order numbers for submitted sales orders",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    salesOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the sales orders to poll for Oracle order numbers"),
  }),

  outputSchema: z.object({
    skipped: z
      .boolean()
      .default(false)
      .describe("Whether the step was skipped (e.g., no sales orders to poll)"),
    skippedReason: z
      .string()
      .optional()
      .describe("Reason for skipping, if skipped is true"),
    results: z
      .array(oracleOrderResultSchema)
      .describe("Results for each sales order polled"),
    allFound: z.boolean().describe("Whether all orders have Oracle numbers"),
    foundCount: z.number().describe("Number of orders with Oracle numbers"),
    notFoundCount: z
      .number()
      .describe("Number of orders still waiting for Oracle numbers"),
  }),

  configSchema: z.object({
    maxWaitTimeMs: z
      .number()
      .default(300000)
      .describe(
        "Maximum time to wait for Oracle order numbers (default: 5 minutes)",
      ),
    initialDelayMs: z
      .number()
      .default(10000)
      .describe("Initial delay between polls (default: 10 seconds)"),
    maxDelayMs: z
      .number()
      .default(60000)
      .describe("Maximum delay between polls (default: 60 seconds)"),
    backoffMultiplier: z
      .number()
      .default(2)
      .describe("Multiplier for exponential backoff (default: 2)"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});

export type OracleOrderResult = z.infer<typeof oracleOrderResultSchema>;
